import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getServiceSLA } from '@/modules/Service/services/sla'
import {
    ServiceSLA,
    SLA_TIER_CONFIG,
    SLATier,
    SLARule,
} from '@/modules/Service/types/sla'
import { Clock, Shield, Timer, TrendingUp } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef } from 'react'
import dayjs, { Dayjs } from 'dayjs'

interface ServiceSLAPreviewProps {
    serviceId: number | undefined
    ticketTypeGroup?: string
    ticketTypeKey?: string
    ticketRequestedAt?: Dayjs | null
    onSLALoaded?: (slaData: {
        responseTimeMinutes?: number
        resolutionTimeMinutes?: number
        sla?: ServiceSLA
    }) => void
}

// Format minutes to readable string
const formatMinutes = (minutes: number | undefined) => {
    if (!minutes) return '-'
    if (minutes < 60) return `${minutes} phút`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} giờ`
}

// Find matching SLA rule for ticket type
const findMatchingSLARule = (
    rules: SLARule[] | undefined,
    ticketTypeGroup?: string,
    ticketTypeKey?: string
): SLARule | undefined => {
    if (!rules?.length) return undefined

    // First try exact match with ticket_type_key
    if (ticketTypeKey) {
        const exactMatch = rules.find(
            (r) =>
                r.ticket_type_group === ticketTypeGroup &&
                r.ticket_type_key === ticketTypeKey
        )
        if (exactMatch) return exactMatch
    }

    // Then try match by group only
    if (ticketTypeGroup) {
        const groupMatch = rules.find(
            (r) => r.ticket_type_group === ticketTypeGroup && !r.ticket_type_key
        )
        if (groupMatch) return groupMatch
    }

    // Return first rule as fallback
    return rules[0]
}

const ServiceSLAPreview: React.FC<ServiceSLAPreviewProps> = ({
    serviceId,
    ticketTypeGroup,
    ticketTypeKey,
    ticketRequestedAt,
    onSLALoaded,
}) => {
    const { t } = useTranslation('services')

    // Fetch SLA for this service
    const { data: slaData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, serviceId, 'sla'],
        queryFn: () => getServiceSLA(serviceId!),
        enabled: !!serviceId && serviceId > 0,
    })

    const sla = slaData?.result as ServiceSLA | undefined
    const hasSLA = !!sla

    // Get tier config
    const tier = sla?.sla_template?.template_tier as SLATier | undefined
    const tierConfig = tier && tier !== 'custom' ? SLA_TIER_CONFIG[tier] : null

    // Find matching rule for ticket type
    const matchingRule = findMatchingSLARule(
        sla?.sla_rules,
        ticketTypeGroup,
        ticketTypeKey
    )

    // Get effective response/resolution times
    const responseTimeMinutes =
        matchingRule?.response_time_minutes ||
        sla?.sla_template?.default_response_time ||
        tierConfig?.responseTimeMinutes
    const resolutionTimeMinutes =
        matchingRule?.resolution_time_minutes ||
        sla?.sla_template?.default_resolution_time ||
        tierConfig?.resolutionTimeMinutes

    // Track if callback has been called for this serviceId
    const lastCalledServiceId = useRef<number | null>(null)

    // Notify parent when SLA is loaded (only once per service)
    useEffect(() => {
        if (
            !isLoading &&
            onSLALoaded &&
            serviceId &&
            lastCalledServiceId.current !== serviceId
        ) {
            lastCalledServiceId.current = serviceId
            onSLALoaded({
                responseTimeMinutes,
                resolutionTimeMinutes,
                sla: sla || undefined,
            })
        }
    }, [
        isLoading,
        serviceId,
        sla,
        responseTimeMinutes,
        resolutionTimeMinutes,
        onSLALoaded,
    ])

    // Reset when serviceId changes
    useEffect(() => {
        if (serviceId !== lastCalledServiceId.current) {
            lastCalledServiceId.current = null
        }
    }, [serviceId])

    // Calculate expected times
    const expectedResponseTime =
        ticketRequestedAt && responseTimeMinutes
            ? dayjs(ticketRequestedAt).add(responseTimeMinutes, 'minute')
            : null
    const expectedCompletionTime =
        ticketRequestedAt && resolutionTimeMinutes
            ? dayjs(ticketRequestedAt).add(resolutionTimeMinutes, 'minute')
            : null

    if (!serviceId || serviceId <= 0) return null

    if (isLoading) {
        return (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-16 w-full" />
            </div>
        )
    }

    if (!hasSLA) {
        return null // No SLA, don't show anything
    }

    const uptime =
        sla.uptime_guarantee ||
        sla.sla_template?.uptime_guarantee ||
        tierConfig?.uptime

    return (
        <div className="p-3 bg-blue-50 rounded-md border border-blue-200 space-y-2">
            {/* Header with tier */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Shield
                        className="w-4 h-4"
                        style={{ color: tierConfig?.color || '#3B82F6' }}
                    />
                    <span className="text-sm font-medium text-blue-900">
                        {t('sla.service_sla', 'SLA Dịch vụ')}
                    </span>
                    {tier && (
                        <Badge
                            variant="outline"
                            className="text-xs"
                            style={{
                                borderColor: tierConfig?.color,
                                color: tierConfig?.color,
                            }}
                        >
                            {tier?.toUpperCase() || ''}
                        </Badge>
                    )}
                </div>
                {matchingRule && (
                    <Badge variant="secondary" className="text-xs">
                        {matchingRule.ticket_type_key ||
                            matchingRule.ticket_type_group ||
                            'Default'}
                    </Badge>
                )}
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-2 text-xs">
                {uptime && (
                    <div className="flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                        <span className="text-gray-500">Uptime:</span>
                        <span className="font-semibold text-green-600">
                            {uptime}%
                        </span>
                    </div>
                )}
                {responseTimeMinutes && (
                    <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-blue-500" />
                        <span className="text-gray-500">
                            {t('sla.response_time', 'Phản hồi')}:
                        </span>
                        <span className="font-medium">
                            {formatMinutes(responseTimeMinutes)}
                        </span>
                    </div>
                )}
                {resolutionTimeMinutes && (
                    <div className="flex items-center gap-1.5">
                        <Timer className="w-3.5 h-3.5 text-purple-500" />
                        <span className="text-gray-500">
                            {t('sla.resolution_time', 'Xử lý')}:
                        </span>
                        <span className="font-medium">
                            {formatMinutes(resolutionTimeMinutes)}
                        </span>
                    </div>
                )}
            </div>

            {/* Expected times based on SLA */}
            {(expectedResponseTime || expectedCompletionTime) && (
                <div className="pt-2 border-t border-blue-200 space-y-1 text-xs">
                    {expectedResponseTime && (
                        <div className="flex items-center gap-1.5 text-gray-600">
                            <Clock className="w-3 h-3 text-blue-400" />
                            <span>
                                {t(
                                    'sla.expected_response',
                                    'TG phản hồi dự kiến'
                                )}
                                :
                            </span>
                            <span className="font-medium text-blue-700">
                                {expectedResponseTime.format(
                                    'DD/MM/YYYY HH:mm'
                                )}
                            </span>
                        </div>
                    )}
                    {expectedCompletionTime && (
                        <div className="flex items-center gap-1.5 text-gray-600">
                            <Timer className="w-3 h-3 text-purple-400" />
                            <span>
                                {t(
                                    'sla.expected_completion',
                                    'TG hoàn thành dự kiến'
                                )}
                                :
                            </span>
                            <span className="font-medium text-purple-700">
                                {expectedCompletionTime.format(
                                    'DD/MM/YYYY HH:mm'
                                )}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default ServiceSLAPreview
